import { apiClient, ApiResponse } from './client'

export type MitigationRung =
  | 'WIDEN_DISPATCH'
  | 'REPRICE'
  | 'SHIFT_PICKUP_WINDOW'
  | 'PREFER_STRONGER_SHIPPER'

export type MitigationEventType =
  | 'LADDER_ENTERED'
  | 'RUNG_ATTEMPTED'
  | 'RUNG_OUTCOME'
  | 'SHIPPER_DECIDED'
  | 'PLACED'
  | 'GAVE_UP'
  | 'OPS_ACTION'

export interface PagedResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export interface FunnelWindow {
  from: string
  to: string
}

export interface FunnelRungStats {
  rung: MitigationRung
  entered: number
  resolved?: number
  resolvedRate?: number
  proposed?: number
  approved?: number
  declined?: number
  expired?: number
}

export interface MatchingFunnelResponse {
  window: FunnelWindow
  requestsConsidered: number
  matchedWithoutLadder: number
  enteredLadder: number
  rungs: FunnelRungStats[]
  gaveUp: { total: number; byReason: Record<string, number> }
  opsPlaced: number
  lostAfterGiveUp: number
}

export interface OfferRoundStats {
  round: number
  offersSent: number
  offersAccepted: number
  acceptanceRate: number
}

function normalizeOfferRoundRow(raw: unknown): OfferRoundStats | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const roundRaw = readField(obj, 'round', 'dispatchRound', 'dispatch_round')
  if (roundRaw == null) return null

  return {
    round: readNumber(roundRaw),
    offersSent: readNumber(readField(obj, 'offersSent', 'offers_sent')),
    offersAccepted: readNumber(readField(obj, 'offersAccepted', 'offers_accepted')),
    acceptanceRate: readNumber(readField(obj, 'acceptanceRate', 'acceptance_rate')),
  }
}

function normalizeOfferRoundList(raw: unknown): OfferRoundStats[] {
  const list = Array.isArray(raw) ? raw : []
  return list.map(normalizeOfferRoundRow).filter((row): row is OfferRoundStats => row != null)
}

export interface AtRiskRow {
  shipmentRequestId: string
  hoursToDeadline?: number
  currentRung?: MitigationRung | null
  rungsAttempted?: MitigationRung[]
  offersSent?: number
  maxDispatchRound?: number
  urgency?: string
  shipperAccountId?: string
  shipperName?: string
  shipperBusinessName?: string
  title?: string
  pickupWindowStart?: string
  giveUpDeadline?: string
  status?: string
  priceEscalationStep?: number
  priceEscalationProposedAt?: string
  priceEscalationApprovedStep?: number
  priceEscalationDecidedAt?: string
  awaitingShipperDecision?: boolean
  matchingAttempts?: number
  offerRounds?: number
}

export interface GivenUpRow {
  shipmentRequestId: string
  givenUpAt?: string
  giveUpReason?: string
  pickupWindowStart?: string
  shipperAccountId?: string
  shipperName?: string
  shipperBusinessName?: string
  title?: string
  urgency?: string
  status?: string
}

export interface MitigationEvent {
  id: string
  shipmentRequestId?: string
  occurredAt: string
  eventType: MitigationEventType | string
  rung?: MitigationRung | null
  detail?: string
  actor?: string
  actorUserId?: string
}

export interface MitigationTimeline {
  shipmentRequestId: string
  events: MitigationEvent[]
  matchingAttemptCount?: number
  offerRoundCount?: number
  jobOffersSentCount?: number
  attemptedMitigationRungs?: MitigationRung[]
  currentRung?: MitigationRung | null
  priceEscalationStep?: number
  priceEscalationApprovedStep?: number
  priceEscalationProposedAt?: string
  priceEscalationDecidedAt?: string
  givenUpAt?: string
  giveUpReason?: string
  deadline?: string
  hoursToDeadline?: number
  unmatchedAlertSentAt?: string
  status?: string
  pickupWindowStart?: string
  pickupAddress?: string
  shipperAccountId?: string
  shipperName?: string
  shipperBusinessName?: string
  urgency?: string
  maxDispatchRound?: number
}

export interface ExtendDeadlineRequest {
  hours: number
  reason?: string
}

export interface ManualPlacementRequest {
  freighterAccountId: string
  vehicleId: string
  driverId: string
  foremanUserId: string
  carrierPayout: number
  shipperPrice?: number
  carrierName?: string
  note?: string
}

export interface FunnelQueryParams {
  from: string
  to: string
  urgency?: string
  accountId?: string
}

export interface GivenUpQueryParams {
  from?: string
  to?: string
  handled?: boolean
  page?: number
  size?: number
}

const BASE = '/api/v1/admin/matching'

function readNumber(value: unknown): number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (!Number.isNaN(parsed)) return parsed
  }
  return 0
}

function readField(raw: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const value = raw[key]
    if (value !== undefined && value !== null) return value
  }
  return undefined
}

function normalizeGaveUp(raw: unknown): { total: number; byReason: Record<string, number> } {
  if (typeof raw === 'number') {
    return { total: raw, byReason: {} }
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    const byReasonRaw = readField(obj, 'byReason', 'by_reason')
    const byReason: Record<string, number> = {}
    if (byReasonRaw && typeof byReasonRaw === 'object') {
      for (const [reason, count] of Object.entries(byReasonRaw as Record<string, unknown>)) {
        byReason[reason] = readNumber(count)
      }
    }
    return {
      total: readNumber(readField(obj, 'total', 'count')),
      byReason,
    }
  }
  return { total: 0, byReason: {} }
}

function normalizeFunnelRung(raw: unknown): FunnelRungStats | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const rung = readField(obj, 'rung') as MitigationRung | undefined
  if (!rung) return null
  const resolved = readField(obj, 'resolved')
  const resolvedRate = readField(obj, 'resolvedRate', 'resolved_rate')
  return {
    rung,
    entered: readNumber(readField(obj, 'entered')),
    resolved: resolved != null ? readNumber(resolved) : undefined,
    resolvedRate: resolvedRate != null ? readNumber(resolvedRate) : undefined,
    proposed: readField(obj, 'proposed') != null ? readNumber(readField(obj, 'proposed')) : undefined,
    approved: readField(obj, 'approved') != null ? readNumber(readField(obj, 'approved')) : undefined,
    declined: readField(obj, 'declined') != null ? readNumber(readField(obj, 'declined')) : undefined,
    expired: readField(obj, 'expired') != null ? readNumber(readField(obj, 'expired')) : undefined,
  }
}

/** Tolerates partial / alternate backend shapes so the funnel UI never crashes on missing counts. */
export function normalizeMatchingFunnelResponse(raw: unknown): MatchingFunnelResponse {
  const payload =
    raw && typeof raw === 'object' && 'data' in raw && (raw as { data?: unknown }).data != null
      ? (raw as { data: unknown }).data
      : raw

  const obj =
    payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {}

  const windowRaw =
    readField(obj, 'window') && typeof readField(obj, 'window') === 'object'
      ? (readField(obj, 'window') as Record<string, unknown>)
      : {}

  const gaveUpRaw =
    readField(obj, 'gaveUp', 'gave_up') ??
    readField(obj, 'gaveUpTotal', 'gave_up_total')

  return {
    window: {
      from: String(readField(windowRaw, 'from') ?? ''),
      to: String(readField(windowRaw, 'to') ?? ''),
    },
    requestsConsidered: readNumber(readField(obj, 'requestsConsidered', 'requests_considered')),
    matchedWithoutLadder: readNumber(readField(obj, 'matchedWithoutLadder', 'matched_without_ladder')),
    enteredLadder: readNumber(readField(obj, 'enteredLadder', 'entered_ladder')),
    rungs: (Array.isArray(obj.rungs) ? obj.rungs : [])
      .map(normalizeFunnelRung)
      .filter((r): r is FunnelRungStats => r != null),
    gaveUp: normalizeGaveUp(gaveUpRaw),
    opsPlaced: readNumber(readField(obj, 'opsPlaced', 'ops_placed')),
    lostAfterGiveUp: readNumber(readField(obj, 'lostAfterGiveUp', 'lost_after_give_up')),
  }
}

function readString(value: unknown): string | undefined {
  if (value == null) return undefined
  const s = String(value)
  return s === '' ? undefined : s
}

function readBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  return undefined
}

function normalizeAtRiskRow(raw: unknown): AtRiskRow | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const shipmentRequestId = readString(
    readField(obj, 'shipmentRequestId', 'shipment_request_id'),
  )
  if (!shipmentRequestId) return null

  const title = readString(readField(obj, 'title'))
  const accountId = readString(
    readField(obj, 'shipperAccountId', 'shipper_account_id', 'accountId', 'account_id'),
  )
  const rungsRaw = readField(obj, 'rungsAttempted', 'rungs_attempted')
  const hours = readField(obj, 'hoursToDeadline', 'hours_to_deadline')

  return {
    shipmentRequestId,
    hoursToDeadline: hours != null ? readNumber(hours) : undefined,
    currentRung: (readField(obj, 'currentRung', 'current_rung') as MitigationRung | null) ?? null,
    rungsAttempted: Array.isArray(rungsRaw) ? (rungsRaw as MitigationRung[]) : [],
    offersSent: readNumber(readField(obj, 'offersSent', 'offers_sent')) || undefined,
    maxDispatchRound:
      readNumber(readField(obj, 'maxDispatchRound', 'max_dispatch_round')) || undefined,
    urgency: readString(readField(obj, 'urgency')),
    shipperAccountId: accountId,
    shipperName: readString(readField(obj, 'shipperName', 'shipper_name')) ?? title,
    shipperBusinessName:
      readString(readField(obj, 'shipperBusinessName', 'shipper_business_name')) ?? title,
    title,
    pickupWindowStart: readString(readField(obj, 'pickupWindowStart', 'pickup_window_start')),
    giveUpDeadline: readString(readField(obj, 'giveUpDeadline', 'give_up_deadline')),
    status: readString(readField(obj, 'status')),
    priceEscalationStep:
      readField(obj, 'priceEscalationStep', 'price_escalation_step') != null
        ? readNumber(readField(obj, 'priceEscalationStep', 'price_escalation_step'))
        : undefined,
    priceEscalationApprovedStep:
      readField(obj, 'priceEscalationApprovedStep', 'price_escalation_approved_step') != null
        ? readNumber(readField(obj, 'priceEscalationApprovedStep', 'price_escalation_approved_step'))
        : undefined,
    priceEscalationProposedAt: readString(
      readField(obj, 'priceEscalationProposedAt', 'price_escalation_proposed_at'),
    ),
    priceEscalationDecidedAt: readString(
      readField(obj, 'priceEscalationDecidedAt', 'price_escalation_decided_at'),
    ),
    awaitingShipperDecision: readBoolean(
      readField(obj, 'awaitingShipperDecision', 'awaiting_shipper_decision'),
    ),
    matchingAttempts:
      readNumber(readField(obj, 'matchingAttempts', 'matching_attempts', 'matchingAttemptCount')) ||
      undefined,
    offerRounds:
      readNumber(readField(obj, 'offerRounds', 'offer_rounds', 'offerRoundCount')) || undefined,
  }
}

function normalizeGivenUpRow(raw: unknown): GivenUpRow | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const shipmentRequestId = readString(
    readField(obj, 'shipmentRequestId', 'shipment_request_id'),
  )
  if (!shipmentRequestId) return null

  const title = readString(readField(obj, 'title'))
  const accountId = readString(
    readField(obj, 'shipperAccountId', 'shipper_account_id', 'accountId', 'account_id'),
  )

  return {
    shipmentRequestId,
    givenUpAt: readString(readField(obj, 'givenUpAt', 'given_up_at')),
    giveUpReason: readString(readField(obj, 'giveUpReason', 'give_up_reason')),
    pickupWindowStart: readString(readField(obj, 'pickupWindowStart', 'pickup_window_start')),
    shipperAccountId: accountId,
    shipperName: readString(readField(obj, 'shipperName', 'shipper_name')) ?? title,
    shipperBusinessName:
      readString(readField(obj, 'shipperBusinessName', 'shipper_business_name')) ?? title,
    title,
    urgency: readString(readField(obj, 'urgency')),
    status: readString(readField(obj, 'status')),
  }
}

/** Backend may return a bare array or a Spring page wrapper. */
function normalizePagedList<T>(
  raw: unknown,
  mapRow: (row: unknown) => T | null,
): PagedResponse<T> {
  if (Array.isArray(raw)) {
    const content = raw.map(mapRow).filter((row): row is T => row != null)
    return {
      content,
      totalElements: content.length,
      totalPages: content.length > 0 ? 1 : 0,
      number: 0,
      size: content.length,
    }
  }

  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    const listRaw = readField(obj, 'content', 'items', 'data')
    const list = Array.isArray(listRaw) ? listRaw : []
    const content = list.map(mapRow).filter((row): row is T => row != null)
    const totalElements = readNumber(readField(obj, 'totalElements', 'total_elements'))
    const totalPages = readNumber(readField(obj, 'totalPages', 'total_pages'))
    const number = readNumber(readField(obj, 'number', 'page'))
    const size = readNumber(readField(obj, 'size', 'pageSize', 'page_size'))

    return {
      content,
      totalElements: totalElements || content.length,
      totalPages: totalPages || (content.length > 0 ? 1 : 0),
      number: number || 0,
      size: size || content.length,
    }
  }

  return { content: [], totalElements: 0, totalPages: 0, number: 0, size: 0 }
}

export const mitigationService = {
  getFunnel: async (params: FunnelQueryParams) => {
    const response = await apiClient.get<ApiResponse<unknown>>(`${BASE}/funnel`, {
      params,
    })
    return normalizeMatchingFunnelResponse(response.data?.data ?? response.data)
  },

  getOfferRounds: async (from: string, to: string) => {
    const response = await apiClient.get<ApiResponse<unknown>>(`${BASE}/offer-rounds`, {
      params: { from, to },
    })
    return normalizeOfferRoundList(response.data?.data ?? response.data)
  },

  getAtRisk: async (withinHours = 6, page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<unknown>>(`${BASE}/at-risk`, {
      params: { withinHours, page, size },
    })
    return normalizePagedList(response.data?.data ?? response.data, normalizeAtRiskRow)
  },

  getGivenUp: async (params: GivenUpQueryParams = {}) => {
    const { from, to, handled = false, page = 0, size = 20 } = params
    const response = await apiClient.get<ApiResponse<unknown>>(`${BASE}/given-up`, {
      params: { from, to, handled, page, size },
    })
    return normalizePagedList(response.data?.data ?? response.data, normalizeGivenUpRow)
  },

  getTimeline: async (shipmentRequestId: string) => {
    const response = await apiClient.get<ApiResponse<MitigationTimeline>>(
      `${BASE}/shipment-requests/${shipmentRequestId}/timeline`,
    )
    return response.data.data
  },

  mitigateNow: async (shipmentRequestId: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `${BASE}/shipment-requests/${shipmentRequestId}/mitigate-now`,
    )
    return response.data
  },

  resetLadder: async (shipmentRequestId: string, reason?: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `${BASE}/shipment-requests/${shipmentRequestId}/reset-ladder`,
      reason ? { reason } : undefined,
    )
    return response.data
  },

  extendDeadline: async (shipmentRequestId: string, request: ExtendDeadlineRequest) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `${BASE}/shipment-requests/${shipmentRequestId}/extend-deadline`,
      request,
    )
    return response.data
  },

  applyPriceEscalation: async (shipmentRequestId: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `${BASE}/shipment-requests/${shipmentRequestId}/price-escalation/apply`,
    )
    return response.data
  },

  manualPlacement: async (shipmentRequestId: string, request: ManualPlacementRequest) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `${BASE}/shipment-requests/${shipmentRequestId}/manual-placement`,
      request,
    )
    return response.data
  },

  returnToMatching: async (shipmentRequestId: string, reason?: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `${BASE}/shipment-requests/${shipmentRequestId}/return-to-matching`,
      reason ? { reason } : undefined,
    )
    return response.data
  },
}
